import { useEffect, useState } from "react";
import { api } from "../../api/client";
import { MONTHS, SESSION_OPTIONS, YEARS, todayParts } from "../../constants";
import { PRESENT_UNRATED, PUNCTUALITY, PUNCTUALITY_STYLES, recapSymbol } from "../../utils/attendance";
import {
  Button,
  DataCard,
  Empty,
  Icon,
  PageTitle,
  Select,
  Spinner,
  Td,
  Th,
  cx
} from "../../components/ui";

// Nama berkas unduhan. Recap yang sudah disaring TIDAK boleh turun dengan nama yang
// sama seperti recap seluruh cabang — admin mengunduh beberapa kelas berturut-turut,
// lalu di folder Unduhan tertumpuk "Recap_Kehadiran_September_2026 (1).xlsx" yang tak
// bisa dibedakan isinya tanpa dibuka satu per satu.
//
// Karakter yang tak sah di nama berkas Windows (\ / : * ? " < > |) dibersihkan, dan
// spasi jadi tanda hubung supaya aman dikirim lewat WhatsApp/email.
function bersihkanNamaBerkas(teks) {
  return String(teks || "")
    .replace(/[\\/:*?"<>|]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

export function namaBerkasRecap({ month, year, kelas, trainer, session }) {
  const bagian = ["Recap", "Kehadiran"];
  // Kelas paling khusus → paling menjelaskan isinya; kalau ada, trainer & sesi sudah
  // terkandung di dalamnya dan tak perlu diulang.
  if (kelas) bagian.push(bersihkanNamaBerkas(kelas));
  else {
    if (trainer && trainer !== "All") bagian.push(bersihkanNamaBerkas(trainer));
    if (session && session !== "All") bagian.push(bersihkanNamaBerkas(session));
  }
  bagian.push(bersihkanNamaBerkas(month), String(year));
  return bagian.filter(Boolean).join("_") + ".xlsx";
}

// Hari ketika sebuah kelas diisi TRAINER PENGGANTI, beserta namanya (indeks 0..30).
//
// Penggantian itu sifatnya PER HARI untuk seluruh kelas, bukan per murid. Karena itu
// penandanya ditaruh sekali di kepala kolom harinya — bukan diulang di tiap sel, yang
// justru membuat 31 kolom penuh badge dan malah sulit dipindai.
//
// Sumbernya sufiks pada sel hari ("Attend:Ms. Bella"): catatan siapa yang BENAR-BENAR
// mengisi, dasar yang sama dengan yang dipakai mesin gaji.
//
// Ditaruh di tingkat modul karena DUA komponen memakainya — RecapPage untuk ekspor Excel
// dan RecapTable untuk tampilan. Satu rumus, dua tempat: kalau digandakan, berkas yang
// diunduh cepat atau lambat akan berselisih dengan yang terlihat di layar.
function pengajarPerHari(rows, coverHari) {
  return Array.from({ length: 31 }, (_, dayIndex) => {
    // Catatan penggantian dari server menang atas tebakan sufiks. Ia berasal dari
    // `daily_coverages` yang TERKUNCI — catatan yang sama yang dibayar mesin gaji —
    // sedangkan sufiks sel baru ada bila yang mengisi berbeda dari pemilik BARIS.
    // Hari yang pemiliknya sudah berpindah, dan seluruh data sebelum sufiks ada,
    // tersimpan tanpa sufiks; harinya jadi terbaca seolah tak pernah digantikan.
    const dariCatatan = String((coverHari || [])[dayIndex] || "").trim();
    if (dariCatatan) return { pengganti: [dariCatatan], pemilik: [] };

    const pengganti = new Set();
    const pemilik = new Set();
    (rows || []).forEach((row) => {
      const isi = String((row.days || [])[dayIndex] || "");
      if (!isi) return;
      const sub = isi.includes(":") ? isi.split(":")[1].trim() : "";
      // Sel bersufiks = diisi orang lain. Tanpa sufiks = diisi pemilik barisnya sendiri,
      // dan nama pemilik itu ada di baris recap-nya — jadi "siapa yang mengajar" bisa
      // dijawab untuk SETIAP hari yang ada absensinya, bukan cuma hari yang digantikan.
      if (sub) pengganti.add(sub);
      else if (String(row.trainer || "").trim()) pemilik.add(String(row.trainer).trim());
    });
    return { pengganti: [...pengganti], pemilik: [...pemilik] };
  });
}

export default function RecapPage({ user, admin = false, setToast }) {
  const today = todayParts();

  const [filters, setFilters] = useState({
    year: today.year,
    month: today.month,
    session: "All",
    trainer: "All",
    // "" = tanpa penyaring kelas. Penyaring Trainer & Sesi SENGAJA tetap ada: admin
    // memakainya untuk melihat SEMUA kelas satu trainer sekaligus saat memeriksa
    // bebannya, dan itu pertanyaan yang berbeda dari "apa yang terjadi di kelas ini".
    classId: ""
  });

  const [trainers, setTrainers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [rows, setRows] = useState([]);
  // Nama pengganti per tanggal, dari catatan penggantian. null = server tak
  // mengirimnya (bukan mode kelas, atau versi lama) → jatuh ke pembacaan sufiks.
  const [coverHari, setCoverHari] = useState(null);
  const [loading, setLoading] = useState(true);

  async function loadTrainerList() {
    if (!admin) return;

    try {
      const [res, br, kls] = await Promise.all([
        api.getTrainerList(),
        api.getBranches(),
        // Butuh hak kelola jadwal cabang. Kalau ditolak, penyaring kelasnya sekadar
        // tak muncul — halaman rekapnya tetap jalan seperti biasa.
        api.getClasses().catch(() => ({ success: false, data: [] }))
      ]);
      setClasses(kls?.success ? (kls.data || []).filter((k) => k.isActive !== false) : []);
      if (res.success) setTrainers(res.data || []);
      if (br?.success) setBranches(br.data || []);
    } catch {
      setTrainers([]);
    }
  }

  async function load() {
    setLoading(true);

    try {
      const res = admin
        ? await api.getAdminRecapData({
            year: filters.year,
            month: filters.month,
            trainer: filters.trainer,
            session: filters.session,
            classId: filters.classId
          })
        : await api.getTrainerRecap({
            userEmail: user.email,
            year: filters.year,
            month: filters.month,
            session: filters.session
          });

      if (res.success) {
        setRows(res.data || []);
        setCoverHari(Array.isArray(res.pengajarHari) ? res.pengajarHari : null);
      } else {
        setToast({
          type: "error",
          message: res.message || "Failed to load recap"
        });
      }
    } catch (err) {
      setToast({
        type: "error",
        message: err.message || "Failed to load recap"
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadTrainerList();
  }, []);

  useEffect(() => {
    load();
  }, [filters]);

  // Unduh recap sebagai .xlsx sesuai format template: Nama Siswa · NO KIP · Meeting ·
  // kolom per HARI KERJA (Sen–Jum) bulan itu (1=hadir/0=tidak) · Total (jumlah hadir).

  // Sama seperti di layar: baris "Pengganti" hanya sah bila yang diekspor SATU kelas.
  // Tanpa itu, hampir tiap hari akan berisi nama dari kelas mana saja yang kebetulan
  // digantikan — informasi yang tak berlaku untuk kebanyakan baris di bawahnya.
  const pengajarHarian = filters.classId
    ? pengajarPerHari(rows, coverHari)
    : Array.from({ length: 31 }, () => ({ pengganti: [], pemilik: [] }));

  async function exportXlsx() {
    if (!rows.length) {
      setToast({ type: "error", message: "Tidak ada data recap untuk diunduh." });
      return;
    }
    try {
      const ExcelJS = (await import("exceljs")).default;

      // Peta periode murid untuk kolom "Meeting" (2x/3x/1x).
      const periodeMap = new Map();
      try {
        const sres = await api.getAllStudents();
        if (sres?.success) {
          (sres.data || []).forEach((s) => periodeMap.set(String(s.studentId), s.periode || ""));
        }
      } catch {
        /* biarkan kosong bila gagal */
      }

      // Kolom = tiap hari kerja (Senin–Jumat) di bulan terpilih.
      const monthIndex = Math.max(0, MONTHS.indexOf(filters.month));
      const yearNum = Number(filters.year);
      const daysInMonth = new Date(yearNum, monthIndex + 1, 0).getDate();
      const WD = { 1: "M", 2: "T", 3: "W", 4: "Th", 5: "F" };
      const dayCols = [];
      for (let d = 1; d <= daysInMonth; d++) {
        const wd = new Date(yearNum, monthIndex, d).getDay();
        if (wd >= 1 && wd <= 5) dayCols.push({ day: d, abbr: WD[wd] });
      }

      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet("Recap");

      ws.addRow(["Nama Siswa", "NO KIP", "Meeting", ...dayCols.map((c) => c.abbr), "Total Gaji"]);
      ws.addRow(["", "", "", ...dayCols.map((c) => c.day), ""]);

      // Baris "Pengganti": hari ketika kelas TIDAK dipegang trainernya sendiri.
      // Ditaruh di kepala tabel, sekali per hari — penggantian itu sifatnya per hari
      // untuk seluruh kelas, bukan per murid. Di layar penandanya sama; berkas yang
      // diunduh tak boleh kehilangan informasi yang ada di layar.
      const adaPenggantiEkspor = pengajarHarian.some(
        (p) => p.pengganti.length > 0 || p.pemilik.length > 0
      );
      const barisPengganti = adaPenggantiEkspor ? ws.addRow([
        "", "", "Pengganti",
        ...dayCols.map((c) => {
          const p = pengajarHarian[c.day - 1] || { pengganti: [], pemilik: [] };
          // Nama pengganti diberi awalan supaya di Excel pun langsung terbaca bahwa
          // hari itu BUKAN trainer kelasnya yang mengajar.
          if (p.pengganti.length) return "(pengganti) " + p.pengganti.join(", ");
          return p.pemilik.join(", ");
        }),
        ""
      ]) : null;
      if (barisPengganti) {
        barisPengganti.eachCell((cell, col) => {
          cell.font = { size: 8, bold: true, color: { argb: "FF7E22CE" } };
          cell.alignment = { horizontal: "center", vertical: "middle" };
          if (col > 3 && String(cell.value || "").trim()) {
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF3E8FF" } };
          }
        });
      }

      // Isi sel pakai simbol yang SAMA dengan layar (recapSymbol): ✓ On Time, L Late,
      // VL Very Late, H hadir tanpa keterangan, I Izin, A Alpha/Absen, kosong = tidak hadir /
      // tidak ada kelas. Total = jumlah hari HADIR, apa pun keterangannya — bukan jumlah ✓.
      let adaTanpaKeteranganEkspor = false;
      rows.forEach((row) => {
        const days = row.days || [];
        let total = 0;
        const cells = dayCols.map((c) => {
          const sel = recapSymbol(days[c.day - 1], (row.punctuality || [])[c.day - 1]);
          if (!sel || sel.kind === "other") return "";
          if (sel.hadir) total++;
          if (sel.kind === "unrated") adaTanpaKeteranganEkspor = true;
          return sel.symbol;
        });
        ws.addRow([
          row.nickname || row.studentId,
          row.studentId,
          periodeMap.get(String(row.studentId)) || "",
          ...cells,
          total
        ]);
      });

      // ── Styling ──
      const totalCol = 3 + dayCols.length + 1;
      // Baris "Pengganti" (bila ada) menggeser data satu baris ke bawah. Dulu tak dihitung,
      // jadi baris murid TERAKHIR tak ikut diberi garis & warna.
      const awalData = barisPengganti ? 4 : 3;
      const lastRow = awalData - 1 + rows.length;

      // Warna per simbol, sama prinsipnya dengan layar: hadir (✓ L VL H) bertint lembut,
      // tidak hadir (I A) menyala. Simbol tiap jenis unik, jadi cukup dibaca dari nilainya.
      const GAYA_SIMBOL = {
        "✓": { fill: "FFDCFCE7", font: "FF16A34A" },
        L: { fill: "FFFEF3C7", font: "FFB45309" },
        VL: { fill: "FFFEE2E2", font: "FFDC2626" },
        H: { fill: "FFF1F5F9", font: "FF475569" },
        I: { fill: "FFF59E0B", font: "FFFFFFFF" },
        A: { fill: "FFEF4444", font: "FFFFFFFF" }
      };

      ws.getColumn(1).width = 26;
      ws.getColumn(2).width = 11;
      ws.getColumn(3).width = 9;
      for (let i = 0; i < dayCols.length; i++) ws.getColumn(4 + i).width = 4.5;
      ws.getColumn(totalCol).width = 11;

      for (let r = 1; r <= lastRow; r++) {
        const rowRef = ws.getRow(r);
        for (let c = 1; c <= totalCol; c++) {
          const cell = rowRef.getCell(c);
          cell.border = {
            top: { style: "thin", color: { argb: "FFD1D5DB" } },
            left: { style: "thin", color: { argb: "FFD1D5DB" } },
            bottom: { style: "thin", color: { argb: "FFD1D5DB" } },
            right: { style: "thin", color: { argb: "FFD1D5DB" } }
          };
          if (r <= 2) {
            cell.alignment = { horizontal: "center", vertical: "middle" };
            cell.font = { bold: true, color: { argb: r === 1 ? "FFFFFFFF" : "FF1F2937" } };
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: r === 1 ? "FF1E3A8A" : "FFDBEAFE" }
            };
          } else if (r < awalData) {
            // Baris "Pengganti": gayanya sudah diatur saat dibuat — cukup garisnya.
          } else if (c >= 4 && c < totalCol) {
            cell.alignment = { horizontal: "center" };
            const gaya = GAYA_SIMBOL[cell.value];
            if (gaya) {
              cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: gaya.fill } };
              cell.font = { bold: true, color: { argb: gaya.font } };
            }
          } else if (c === totalCol) {
            cell.alignment = { horizontal: "center" };
            cell.font = { bold: true };
          }
        }
      }
      ws.getCell("A1").alignment = { horizontal: "left", vertical: "middle" };
      ws.views = [{ state: "frozen", xSplit: 3, ySplit: 2 }];

      // ── Bagian perhitungan gaji (seperti template) ──
      // Jumlah murid hadir per hari (informasional).
      const dailyHeadcount = dayCols.map((c) =>
        rows.reduce(
          (sum, row) =>
            sum + (String((row.days || [])[c.day - 1] || "").split(":")[0] === "Attend" ? 1 : 0),
          0
        )
      );

      // Gaji harian (Rp) = tarif tier aplikasi per hari, diambil dari perhitungan gaji
      // aplikasi (getAdminSalaryData → trainer_salary), agar SAMA dengan halaman Payroll.
      // Hanya bermakna saat difilter ke satu trainer (gaji = per trainer/sesi).
      let dailySalary = null;
      let totalSalary = 0;
      if (admin && filters.trainer && filters.trainer !== "All") {
        try {
          const sal = await api.getAdminSalaryData({
            year: filters.year,
            month: filters.month,
            trainer: filters.trainer
          });
          if (sal?.success) {
            const perDay = new Array(31).fill(0);
            (sal.data || []).forEach((r) =>
              (r.days || []).forEach((v, i) => {
                perDay[i] += Number(v || 0);
              })
            );
            dailySalary = dayCols.map((c) => perDay[c.day - 1]);
            totalSalary = dailySalary.reduce((a, b) => a + b, 0);
          }
        } catch {
          /* abaikan — tanpa baris gaji */
        }
      }

      const styleSummaryRow = (rowRef, rupiah) => {
        rowRef.font = { bold: true };
        for (let c = 1; c <= totalCol; c++) {
          const cell = rowRef.getCell(c);
          cell.border = {
            top: { style: "thin", color: { argb: "FFD1D5DB" } },
            left: { style: "thin", color: { argb: "FFD1D5DB" } },
            bottom: { style: "thin", color: { argb: "FFD1D5DB" } },
            right: { style: "thin", color: { argb: "FFD1D5DB" } }
          };
          if (c >= 4) cell.alignment = { horizontal: "center" };
          if (rupiah && c >= 4) cell.numFmt = '"Rp"#,##0';
        }
        rowRef.getCell(1).alignment = { horizontal: "left" };
        rowRef.getCell(1).fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: rupiah ? "FFFEF3C7" : "FFF1F5F9" }
        };
      };

      ws.addRow([]); // pemisah
      styleSummaryRow(
        ws.addRow([
          "Jumlah Murid / Hari",
          "",
          "",
          ...dailyHeadcount,
          dailyHeadcount.reduce((a, b) => a + b, 0)
        ]),
        false
      );
      if (dailySalary) {
        styleSummaryRow(
          ws.addRow(["Gaji Harian", "", "", ...dailySalary, totalSalary]),
          true
        );
      }

      ws.addRow([]);
      const legend = ws.addRow([
        "Keterangan:  ✓ = On Time   ·   L = Late   ·   VL = Very Late"
          + (adaTanpaKeteranganEkspor ? "   ·   H = hadir tanpa keterangan" : "")
          + "   (semuanya hadir & dihitung di Total)   ·   I = Izin   ·   A = Alpha/Absen"
          + "   ·   (kosong) = tidak hadir / tidak ada kelas"
          + "   ·   baris \"Pengganti\" = hari itu kelas diisi trainer lain"
      ]);
      legend.getCell(1).font = { italic: true, color: { argb: "FF64748B" } };

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const kelasTerpilih = classes.find(
        (k) => String(k.id) === String(filters.classId)
      );
      a.download = namaBerkasRecap({
        month: filters.month,
        year: filters.year,
        kelas: kelasTerpilih
          ? `${kelasTerpilih.session} ${kelasTerpilih.trainerText || ""}`
          : "",
        trainer: filters.trainer,
        session: filters.session
      });
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      setToast({
        type: "success",
        message: dailySalary
          ? "Excel recap + gaji harian berhasil diunduh."
          : "Excel recap diunduh. Filter ke satu trainer untuk menyertakan baris gaji harian."
      });
    } catch (err) {
      setToast({ type: "error", message: "Gagal membuat Excel: " + (err.message || err) });
    }
  }

  return (
    <>
      <PageTitle
        title={admin ? "Global Recap" : "Monthly Recap"}
        subtitle={
          admin
            ? "Filter system-wide attendance and export to CSV."
            : "Track student attendance trends across the entire month."
        }
        action={
          <div className="flex flex-wrap gap-2">
            <Select
              value={filters.year}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  year: e.target.value
                })
              }
              className="w-28"
            >
              {YEARS.map((year) => (
                <option key={year}>{year}</option>
              ))}
            </Select>

            <Select
              value={filters.month}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  month: e.target.value
                })
              }
              className="w-40"
            >
              {MONTHS.map((month) => (
                <option key={month}>{month}</option>
              ))}
            </Select>

            {admin && (
              <Select
                value={filters.trainer}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    trainer: e.target.value
                  })
                }
                className="w-40"
                disabled={!!filters.classId}
                title={
                  filters.classId
                    ? "Kelas sudah menentukan trainernya"
                    : undefined
                }
              >
                <option>All</option>
                {trainers.map((trainer) => (
                  <option key={trainer}>{trainer}</option>
                ))}
              </Select>
            )}

            <Select
              value={filters.session}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  session: e.target.value
                })
              }
              className="w-44"
              disabled={!!filters.classId}
              title={filters.classId ? "Kelas sudah menentukan sesinya" : undefined}
            >
              <option>All</option>
              {SESSION_OPTIONS.map((session) => (
                <option key={session}>{session}</option>
              ))}
            </Select>

            {/* Penyaring KELAS. Menemani Trainer & Sesi, tidak menggantikannya: saat
                dipilih, rekapnya menampilkan bulan kelas itu APA ADANYA — termasuk hari
                yang diisi trainer pengganti, dan murid yang pindah tengah bulan. */}
            {admin && !!classes.length && (
              <Select
                value={filters.classId}
                onChange={(e) =>
                  // Trainer & Sesi dikembalikan ke All: kelas sudah menentukan keduanya,
                  // dan sisa pilihan lama akan diam-diam mempersempit hasilnya.
                  setFilters({
                    ...filters,
                    classId: e.target.value,
                    ...(e.target.value ? { trainer: "All", session: "All" } : {})
                  })
                }
                className="w-56"
                title="Lihat rekap satu kelas utuh"
              >
                <option value="">Semua kelas</option>
                {classes.map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.session} · {k.trainerText || "(tanpa trainer)"}
                  </option>
                ))}
              </Select>
            )}

            {admin && (
              <Button variant="success" onClick={exportXlsx}>
                <Icon name="download" />
                Excel
              </Button>
            )}
          </div>
        }
      />

      {loading ? (
        <Spinner />
      ) : (
        <RecapTable
          rows={rows}
          admin={admin}
          branches={branches}
          year={filters.year}
          month={filters.month}
          perKelas={!!filters.classId}
          coverHari={coverHari}
        />
      )}
    </>
  );
}

const DAY_LABELS = ["Min", "Sen", "Sel", "Rab", "Kam", "Jum", "Sab"];

function RecapTable({ rows, admin, branches = [], year, month, perKelas = false, coverHari = null }) {
  // Penanda per-HARI hanya berlaku bila yang dilihat SATU kelas.
  //
  // "Tanggal 3 diisi Mr. Reza" itu pernyataan tentang sebuah kelas. Di tampilan
  // se-cabang, tiap hari hampir selalu ada SATU kelas yang digantikan, sehingga kepala
  // kolomnya penuh nama yang tak berlaku untuk kebanyakan baris — dan nama itu menimpa
  // label hari, jadi admin malah kehilangan MIN/SAB-nya. Ringkasannya pun jadi dua baris
  // penuh badge yang tak menjawab apa pun.
  //
  // Penanda per SEL tetap hidup di semua mode: ia menempel pada barisnya sendiri, jadi
  // pernyataannya selalu benar berapa pun cakupan yang sedang dilihat.
  const pengajarHarian = perKelas
    ? pengajarPerHari(rows, coverHari)
    : Array.from({ length: 31 }, () => ({ pengganti: [], pemilik: [] }));
  const adaPengganti = pengajarHarian.some((p) => p.pengganti.length > 0);
  if (!rows.length) {
    return <Empty label="No records found for this period." />;
  }

  const branchById = new Map(branches.map((b) => [b.id, b]));
  // Chip cabang hanya saat data mencakup >1 cabang (mode "Semua" super admin).
  const showBranch = admin && new Set(rows.map((r) => r.branchId)).size > 1;

  const yearNum = Number(year);
  const monthIndex = Math.max(0, MONTHS.indexOf(month));
  const daysInMonth = new Date(yearNum, monthIndex + 1, 0).getDate();

  // Jumlah murid HADIR per hari (base status "Attend" — termasuk coverage & tambahan).
  const dailyCounts = Array.from({ length: 31 }, (_, dayIndex) =>
    rows.reduce((sum, row) => {
      const status = String((row.days || [])[dayIndex] || "").split(":")[0];
      return sum + (status === "Attend" ? 1 : 0);
    }, 0)
  );


  // Hadir yang tak punya keterangan (absensi lama tanpa jam) jarang ada — simbol H-nya
  // hanya dijelaskan di legenda bila memang muncul.
  const adaTanpaKeterangan = rows.some((row) =>
    (row.days || []).some((d, i) => recapSymbol(d, (row.punctuality || [])[i])?.kind === "unrated")
  );

  // Simbol sel = recapSymbol, yang juga dipakai ekspor Excel: ✓ On Time · L Late ·
  // VL Very Late · H hadir tanpa keterangan · I Izin · A tidak masuk.
  function formatCell(raw, punctuality, dariTambahan) {
    if (!raw) return null;

    const substitute = String(raw).split(":")[1];
    const sel = recapSymbol(raw, punctuality);

    // HADIR dibuat TENANG, tidak-hadir dibuat MENYALA. Sebulan berisi 31 kolom yang
    // hampir semuanya hadir; kalau yang umum ikut mencolok, yang perlu diperiksa justru
    // tenggelam. Late & Very Late tetap tint lembut — muridnya HADIR; yang menyala hanya
    // Izin dan tidak masuk.
    const warna = sel.hadir
      ? (PUNCTUALITY_STYLES[sel.kind] || PRESENT_UNRATED).soft
      : sel.kind === "izin"
      ? "bg-amber-500 text-white ring-1 ring-amber-600/40"
      : "bg-red-500 text-white ring-1 ring-red-600/40";

    return (
      <div
        title={dariTambahan ? "Hari ini murid diabsen lewat \"Tambah Murid\"" : undefined}
        className={cx(
          "flex flex-col items-center",
          // Hari yang datang dari fitur "Tambah Murid" — mis. sebelum muridnya resmi
          // terdaftar di kelas ini. Ditandai PER HARI, bukan dengan menandai seluruh
          // barisnya: sesudah digabung, satu baris bisa memuat kedua asal sekaligus.
          dariTambahan && "rounded ring-1 ring-dashed ring-purple-400/70"
        )}
      >
        <span
          title={sel.label}
          className={cx(
            "flex h-6 min-w-6 items-center justify-center rounded px-1 text-[10px] font-black",
            warna
          )}
        >
          {sel.symbol}
        </span>

        {/* Dulu cuma "SUB" 8px dan namanya DIBUANG — variabel `substitute` diurai lalu
            tak dipakai. Admin jadi tahu "bukan dia" tapi tak tahu siapa, padahal itu
            yang dicari saat memeriksa hari yang digantikan. */}
        {substitute && (
          <span
            title={`Diisi oleh ${substitute}`}
            className="mt-0.5 max-w-[56px] truncate rounded bg-purple-500/15 px-1 text-[9px] font-black text-purple-600 dark:text-purple-400"
          >
            {substitute}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Legenda SIMBOL. Sebelumnya hanya ada di berkas Excel, sementara layar memakai
          skema lain — huruf yang sama berarti hal berlawanan di dua tempat. Sekarang
          satu skema, dan keterangannya ikut tampil di layar. */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] font-bold text-slate-500">
        <span className="uppercase tracking-wide">Keterangan:</span>
        {[...PUNCTUALITY.map((k) => PUNCTUALITY_STYLES[k]), ...(adaTanpaKeterangan ? [PRESENT_UNRATED] : [])].map((s) => (
          <span key={s.short} className="inline-flex items-center gap-1.5">
            <span className={cx("flex h-5 min-w-5 items-center justify-center rounded px-1 text-[10px] font-black", s.soft)}>
              {s.short}
            </span>
            {s.label}
          </span>
        ))}
        <span className="text-slate-400">(semuanya hadir)</span>
        <span className="inline-flex items-center gap-1.5">
          <span className="flex h-5 min-w-5 items-center justify-center rounded bg-amber-500 text-[10px] font-black text-white">
            I
          </span>
          Izin
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="flex h-5 min-w-5 items-center justify-center rounded bg-red-500 text-[10px] font-black text-white">
            A
          </span>
          Tidak masuk
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="rounded bg-slate-500/10 px-1 text-[9px] font-black text-slate-500">
            nama
          </span>
          Diajar trainer kelasnya
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="rounded bg-purple-500/15 px-1 text-[9px] font-black text-purple-600">
            nama
          </span>
          Diisi trainer pengganti
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="flex h-5 min-w-5 items-center justify-center rounded text-[10px] font-black ring-1 ring-dashed ring-purple-400/70">
            ✓
          </span>
          Diabsen lewat &quot;Tambah Murid&quot;
        </span>
        <span className="text-slate-400">(kosong) = tidak ada kelas / belum diisi</span>
      </div>

      {/* Ringkasan sebulan: admin sering hanya ingin tahu "berapa hari kelas ini tidak
          dipegang trainernya, dan oleh siapa" tanpa memindai 31 kolom. */}
      {adaPengganti && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-purple-300/50 bg-purple-500/5 px-3 py-2 text-[11px] font-bold text-purple-700 dark:border-purple-700/40 dark:text-purple-300">
          <Icon name="swap_horiz" className="text-sm" />
          <span>
            {pengajarHarian.filter((p) => p.pengganti.length > 0).length} hari diisi trainer pengganti:
          </span>
          {pengajarHarian.map((p, i) =>
            p.pengganti.length ? (
              <span key={i} className="rounded bg-purple-500/15 px-1.5 py-0.5 font-black">
                {i + 1} · {p.pengganti.join(", ")}
              </span>
            ) : null
          )}
        </div>
      )}

      <DataCard>
      {/* Tinggi dibatasi → hanya beberapa baris tampil, sisanya di-scroll (vertikal &
          horizontal). Header menempel di atas, baris "Jumlah Murid" menempel di bawah. */}
      <div className="max-h-[480px] overflow-auto">
      <table className="w-full whitespace-nowrap text-left">
        <thead className="sticky top-0 z-20">
          <tr>
            <Th sticky>STUDENT</Th>
            {Array.from({ length: 31 }, (_, index) => {
              const day = index + 1;
              const valid = day <= daysInMonth;
              const weekday = valid
                ? new Date(yearNum, monthIndex, day).getDay()
                : null;
              const isWeekend = weekday === 0 || weekday === 6;

              const ajar = valid ? pengajarHarian[index] : { pengganti: [], pemilik: [] };
              const digantikan = ajar.pengganti.length > 0;
              const pengajar = digantikan ? ajar.pengganti : ajar.pemilik;
              const adaKelas = pengajar.length > 0;

              return (
                <Th key={index} center>
                  <div
                    title={
                      digantikan
                        ? `Digantikan ${pengajar.join(", ")}`
                        : adaKelas
                        ? `Diajar ${pengajar.join(", ")}`
                        : undefined
                    }
                    className={cx(
                      "flex flex-col items-center leading-tight",
                      digantikan
                        ? "-mx-1 rounded bg-purple-500/15 px-1 py-0.5"
                        : adaKelas && "-mx-1 rounded bg-slate-500/10 px-1 py-0.5"
                    )}
                  >
                    <span
                      className={cx(
                        !valid && "text-slate-300 dark:text-slate-700",
                        digantikan && "text-purple-700 dark:text-purple-300"
                      )}
                    >
                      {day}
                    </span>
                    {adaKelas ? (
                      // Nama pengajar menggantikan label hari: di kolom sesempit ini hanya
                      // satu yang muat, dan "siapa yang mengajar" lebih berguna daripada
                      // "hari apa" – tanggalnya sudah tertera di atasnya.
                      //
                      // WARNANYA yang membedakan: ungu bila digantikan, abu-abu bila
                      // trainer kelasnya sendiri yang mengajar. Hari tanpa absensi sama
                      // sekali tetap memakai label hari biasa.
                      <span
                        className={cx(
                          "max-w-[44px] truncate text-[9px] font-black",
                          digantikan
                            ? "text-purple-600 dark:text-purple-400"
                            : "text-slate-500 dark:text-slate-400"
                        )}
                      >
                        {pengajar.join(", ")}
                      </span>
                    ) : (
                      <span
                        className={cx(
                          "text-[9px] font-bold",
                          !valid
                            ? "text-slate-300 dark:text-slate-700"
                            : isWeekend
                            ? "text-red-400"
                            : "text-slate-400"
                        )}
                      >
                        {valid ? DAY_LABELS[weekday] : "–"}
                      </span>
                    )}
                  </div>
                </Th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {rows.map((row, index) => (
            <tr
              key={`${row.studentId}-${index}`}
              className="border-t border-slate-200 hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-primary/5"
            >
              <Td sticky>
                {/* Sesi ditaruh SEBARIS dengan nama, sebagai badge.
                    Recap disimpan satu baris per (murid, sesi), jadi murid yang ikut dua
                    sesi memang punya dua baris berdampingan — dan daftarnya diurutkan
                    (trainer, nickname) sehingga keduanya bertetangga. Dulu satu-satunya
                    pembeda adalah teks 10px abu-abu di bawahnya, jadi barisnya terbaca
                    sebagai nama dobel padahal benar. Paling parah saat trainernya sama
                    juga: di BR04 ada lima murid Mr. Faisal yang cuma beda sesi. */}
                <p className="flex flex-wrap items-center gap-1.5 font-black">
                  <span>{row.nickname || row.studentId}</span>
                  {row.session && (
                    <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-black text-primary">
                      {row.session}
                    </span>
                  )}
                  {row.isAddition && (
                    <span className="rounded-full bg-purple-500/15 px-1.5 py-0.5 text-[9px] font-black text-purple-600">
                      + Tambahan
                    </span>
                  )}
                  {/* Terdaftar di kelas ini tapi belum pernah diabsen bulan ini. Tanpa
                      penanda, barisnya yang serba kosong tak bisa dibedakan dari murid
                      yang memang tak pernah hadir. */}
                  {row.belumDiabsen && (
                    <span className="rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[9px] font-black text-amber-600">
                      Belum diabsen
                    </span>
                  )}
                  {/* Murid yang sudah pindah ke kelas lain di jam yang sama. Ia tetap tampil
                      di sini karena hari-harinya saat masih di kelas ini nyata dan ikut
                      dihitung gaji — yang tampil hanya hari-hari itu. */}
                  {row.pindahKe && (
                    <span
                      title={`Sudah pindah ke kelas ${row.pindahKe}. Yang tampil di sini hanya hari-harinya di kelas ini.`}
                      className="rounded-full bg-sky-500/15 px-1.5 py-0.5 text-[9px] font-black text-sky-600"
                    >
                      Pindah ke {row.pindahKe}
                    </span>
                  )}
                  {/* Kebalikannya: murid pindahan. Hari-hari sebelum ia masuk tercatat di
                      kelas lamanya, jadi sengaja tak tampil di sini. */}
                  {row.hariDiKelasLain > 0 && (
                    <span
                      title={`${row.hariDiKelasLain} hari absensinya bulan ini tercatat di kelas lamanya, jadi tidak tampil di sini.`}
                      className="rounded-full bg-sky-500/15 px-1.5 py-0.5 text-[9px] font-black text-sky-600"
                    >
                      Pindahan
                    </span>
                  )}
                </p>
                <p className="text-[10px] text-slate-500">
                  {showBranch && (
                    <span className="mr-1.5 rounded bg-slate-100 px-1.5 py-0.5 font-bold text-slate-500 dark:bg-slate-800">
                      {branchById.get(row.branchId)?.code || `#${row.branchId}`}
                    </span>
                  )}
                  {/* Student ID DITAMBAHKAN, bukan digantikan. Dulu admin kehilangan ID
                      muridnya — padahal itu cara tercepat memisahkan dua nama yang mirip. */}
                  {admin
                    ? [row.studentId, row.trainer].filter(Boolean).join(" · ")
                    : row.studentId}
                </p>
              </Td>

              {Array.from({ length: 31 }, (_, dayIndex) => (
                <Td key={dayIndex} center>
                  {formatCell(
                    (row.days || [])[dayIndex],
                    (row.punctuality || [])[dayIndex],
                    (row.additionDays || []).includes(dayIndex)
                  )}
                </Td>
              ))}
            </tr>
          ))}
        </tbody>

        <tfoot className="sticky bottom-0 z-20">
          <tr className="border-t-2 border-slate-300 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/60">
            <Td sticky>
              <p className="font-black text-primary">Jumlah Murid</p>
              <p className="text-[10px] text-slate-500">Total hadir / hari</p>
            </Td>

            {Array.from({ length: 31 }, (_, dayIndex) => {
              const valid = dayIndex + 1 <= daysInMonth;
              const count = dailyCounts[dayIndex];
              return (
                <Td key={dayIndex} center className="bg-white dark:bg-slate-900">
                  {valid ? (
                    <span
                      className={cx(
                        "inline-flex h-6 min-w-6 items-center justify-center rounded px-1 text-[11px] font-black tabular-nums",
                        count > 0
                          ? "bg-primary/15 text-primary"
                          : "text-slate-300 dark:text-slate-700"
                      )}
                    >
                      {count}
                    </span>
                  ) : (
                    <span className="text-slate-300 dark:text-slate-700">–</span>
                  )}
                </Td>
              );
            })}
          </tr>
        </tfoot>
      </table>
      </div>
      </DataCard>
    </div>
  );
}